<script setup lang="ts">
/**
 * Virtual Profiler's background: the module's identity, drawn behind every one of its screens.
 *
 * A wash of the product accent, greener at the bottom left and the top right and white at the top
 * left, and over it a mesh of points laid out on the diagonal, like the landmarks the gesture
 * detail draws on a face. A wave crosses the mesh slowly, as light moving across the points
 * (product owner, 2026-10-14: no shapes, no figures; faint, and slow).
 *
 * ## Where it is drawn
 *
 * The module nav teleports it into the shell's `#section-backdrop`, so it lives exactly as long as
 * the nav: it appears on entering the module, goes on leaving it, and does not restart between two
 * of its screens. The shell's slot covers the whole window, sidebar included, so entering the module
 * changes the whole platform (product owner, 2026-10-14), and the cards scroll over a background
 * that stays put.
 *
 * ## Everything over it in glass
 *
 * While it is on screen, the content stops painting its own background, and the sidebar, the cards
 * and a detail sliding in turn translucent and blur what is behind them (the unscoped style below,
 * keyed on the backdrop being in the page). Nothing outside the module changes: the rules only
 * match while this component exists.
 *
 * ## Why a canvas
 *
 * Over a wide screen the mesh is a few thousand points, each with its own brightness every frame:
 * as SVG nodes animated by CSS that is a few thousand animations; on a canvas it is one loop. The
 * wave is slow enough that a dozen frames a second look continuous, so that is all it draws. It
 * draws one still frame for anybody who asked their system for reduced motion, and none while the
 * tab is hidden (the browser stops the frames).
 */
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';

/** Distance between two points of a row, in CSS pixels. Rows sit half of it apart, offset by half. */
const SPACING = 24;

/** One crest to the next, along the diagonal, in CSS pixels. */
const WAVELENGTH = 700;

/** Seconds for a crest to travel one wavelength. */
const PERIOD = 20;

/** Milliseconds between two frames: plenty for a wave this slow. */
const FRAME_MS = 80;

const root = useTemplateRef<HTMLElement>('root');
const canvas = useTemplateRef<HTMLCanvasElement>('canvas');

let frame = 0;
let lastDrawn = 0;
let resizeObserver: ResizeObserver | null = null;
let reducedMotion: MediaQueryList | null = null;

/** How lit a point is at a moment, 0–1: the main wave, crossed by a slower one on the other diagonal. */
function brightness(x: number, y: number, seconds: number): number {
  const k = (2 * Math.PI) / WAVELENGTH;
  const main = Math.sin((x + y) * k - (seconds * 2 * Math.PI) / PERIOD);
  const cross = Math.sin((x - y) * k * 0.6 + (seconds * 2 * Math.PI) / (PERIOD * 1.7));
  return Math.max(0, Math.min(1, 0.5 + 0.4 * main + 0.15 * cross));
}

function draw(seconds: number): void {
  const el = canvas.value;
  const box = root.value;
  if (!el || !box) return;

  const context = el.getContext('2d');
  if (!context) return;

  const ratio = window.devicePixelRatio || 1;
  const width = el.clientWidth;
  const height = el.clientHeight;
  if (el.width !== Math.round(width * ratio) || el.height !== Math.round(height * ratio)) {
    el.width = Math.round(width * ratio);
    el.height = Math.round(height * ratio);
  }

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, width, height);
  // The accent as the theme has it now: read every frame, so switching to dark follows at once.
  context.fillStyle = getComputedStyle(box).color;

  const rowStep = SPACING / 2;
  for (let row = 0, y = rowStep / 2; y < height + rowStep; row++, y += rowStep) {
    const offset = row % 2 === 0 ? 0 : SPACING / 2;
    for (let x = offset; x < width + SPACING; x += SPACING) {
      const lit = brightness(x, y, seconds);
      context.globalAlpha = 0.025 + 0.12 * lit * lit;
      context.beginPath();
      context.arc(x, y, 0.8 + 0.6 * lit, 0, 2 * Math.PI);
      context.fill();
    }
  }
  context.globalAlpha = 1;
}

function loop(now: number): void {
  frame = requestAnimationFrame(loop);
  if (now - lastDrawn < FRAME_MS) return;
  lastDrawn = now;
  draw(now / 1000);
}

/** Moving, or one still frame for anybody who asked for reduced motion. */
function start(): void {
  cancelAnimationFrame(frame);
  if (reducedMotion?.matches) draw(0);
  else frame = requestAnimationFrame(loop);
}

onMounted(() => {
  reducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  reducedMotion?.addEventListener('change', start);
  // A still frame has to be drawn again when the screen changes size; a moving one does it anyway.
  resizeObserver = new ResizeObserver(() => {
    if (reducedMotion?.matches) draw(0);
  });
  if (canvas.value) resizeObserver.observe(canvas.value);
  start();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  reducedMotion?.removeEventListener('change', start);
  resizeObserver?.disconnect();
});
</script>

<template>
  <div ref="root" aria-hidden="true" class="vp-backdrop size-full overflow-hidden text-p-accent">
    <div
      class="absolute -top-1/4 -right-1/5 size-[80vh] rounded-full bg-radial from-p-accent/5 to-transparent to-70%"
    />
    <div
      class="absolute -bottom-1/4 -left-1/5 size-[70vh] rounded-full bg-radial from-p-accent/4 to-transparent to-70%"
    />
    <canvas ref="canvas" class="absolute inset-0 size-full" />
  </div>
</template>

<!--
  Not scoped: the glass rule reaches the cards of whichever page is on screen, which belong to other
  components, and only while the backdrop is in the page.
-->
<style>
/* The content and the sidebar's frame stop painting over the backdrop. */
body:has(.vp-backdrop) main[data-slot='sidebar-inset'],
body:has(.vp-backdrop) [data-slot='sidebar'] > div {
  background-color: transparent;
}

/* The sidebar's panel in glass. */
body:has(.vp-backdrop) [data-sidebar='sidebar'] {
  background-color: color-mix(in oklab, var(--sidebar) 60%, transparent);
  backdrop-filter: blur(14px) saturate(1.15);
}

/*
 * The page's cards in glass, except those over other content rather than over the backdrop: a
 * card over the face stage (which paints its own black, so glass turns it grey) and the
 * configuration's detail panel over its map (product owner, 2026-10-14).
 */
body:has(.vp-backdrop) main :is([data-slot='card'], .bg-card):not([data-rule-panel], .stage *) {
  background-color: color-mix(in oklab, var(--card) 62%, transparent);
  backdrop-filter: blur(14px) saturate(1.15);
}

/* The well some screens set their cards in: lighter still, so the mesh reaches the cards. */
body:has(.vp-backdrop) main .bg-surface {
  background-color: color-mix(in oklab, var(--color-surface) 40%, transparent);
  backdrop-filter: blur(6px);
}

/*
 * A detail while it slides in: as clear as the page it lands as, glass cards over the mesh. A
 * frosted veil read as a white sheet that vanished on landing. The page it covers is hidden instead
 * (the arriving page is inserted right after it), and is removed when the slide lands.
 */
body:has(.vp-backdrop) .page-over {
  background: transparent;
}

body:has(.vp-backdrop) :not(.page-over):has(+ .page-over) {
  visibility: hidden;
}

/*
 * A detail while it slides back out, over the page it is left for (inserted right after it):
 * frosted enough to hide that page, so the two do not read through each other.
 */
body:has(.vp-backdrop) .page-over:has(+ *) {
  background: color-mix(in oklab, var(--background) 85%, transparent);
  backdrop-filter: blur(24px);
}
</style>
